import { useSyncExternalStore } from 'react'

/**
 * Singleton midnight clock — one timer shared by all subscribers.
 * At midnight, bumps a counter and notifies all components at once.
 */
let dateKey = new Date().toDateString()
let timer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function schedule() {
  if (timer) clearTimeout(timer)
  const now = new Date()
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1)
  timer = setTimeout(() => {
    dateKey = new Date().toDateString()
    listeners.forEach(fn => fn())
    schedule()
  }, midnight.getTime() - now.getTime())
}

function subscribe(callback: () => void) {
  if (listeners.size === 0) schedule()
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
    if (listeners.size === 0 && timer) {
      clearTimeout(timer)
      timer = null
    }
  }
}

function getSnapshot() {
  return dateKey
}

/**
 * Den Tag neu prüfen. Der Mitternachts-Timer verschläft, wenn das Gerät schläft (Handy über Nacht, Laptop
 * im Ruhezustand) — wer wieder aufwacht, ruft das, und alles Tagesabhängige rückt nach. Am selben Tag
 * passiert nichts.
 */
export function refreshDay() {
  const today = new Date().toDateString()
  if (today === dateKey) return
  dateKey = today
  listeners.forEach(fn => fn())
  if (listeners.size > 0) schedule()
}

/**
 * Forces a re-render at midnight so date-relative labels
 * (e.g. "Morgen" → "Heute") update automatically — and, as a `deps` key, lets day-relative live
 * queries (the sidebar's „Demnächst“ count, #309) run again on the new day.
 *
 * Uses a single shared timer regardless of how many components subscribe.
 */
export function useMidnightRefresh(): string {
  return useSyncExternalStore(subscribe, getSnapshot)
}
